import { differenceInDays } from "date-fns";
import { defineMessages } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import PlatformFilterOperator from "@mapmycustomers/shared/enum/PlatformFilterOperator";
import { LegacyFilterCondition } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import {
  Condition,
  PlatformFilterCondition,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import i18nService from "@app/config/I18nService";
import EmptyFilter from "@app/util/filters/EmptyFilter";
import NoCheckInFilter, { NO_CHECK_INS_FILTER_OPERATORS } from "@app/util/filters/NoCheckInFilter";
import loggingService from "@app/util/logging";
import { parseApiDate } from "@app/util/parsers";
import { isCombinedPlatformCondition, isLegacyCombinedCondition } from "@app/util/viewModel/assert";
import parseCheckInCutoffDateCondition from "@app/util/viewModel/converters/parseCheckInCutoffDateCondition";
import parseIntervalCondition from "@app/util/viewModel/converters/parseIntervalCondition";
import { getRegularFieldConditionValue } from "@app/util/viewModel/convertFromPlatformFilterModel";
import {
  filterOperatorToPlatformFilterOperatorMap,
  getRegularFieldPlatformConditionValue,
} from "@app/util/viewModel/convertToPlatformFilterModel";
import { isIntervalCondition } from "@app/util/viewModel/util";

import Field, { FieldProperties } from "./Field";

// operators are reversed for "no check-in since" (-2 days)
const swappedFilterOperatorToPlatformFilterOperatorMap: Partial<{
  [key in FilterOperator]: PlatformFilterOperator;
}> = {
  [FilterOperator.GREATER_THAN]: PlatformFilterOperator.LESS_THAN,
  [FilterOperator.GREATER_THAN_OR_EQUAL]: PlatformFilterOperator.LESS_THAN_OR_EQUAL,
  [FilterOperator.LESS_THAN]: PlatformFilterOperator.GREATER_THAN,
  [FilterOperator.LESS_THAN_OR_EQUAL]: PlatformFilterOperator.GREATER_THAN_OR_EQUAL,
} as const;

const messages = defineMessages({
  noCheckIn: {
    id: "noCheckInField.value",
    defaultMessage: "{days} {days, plural, one {day} other {days}}",
    description: "No check in days message for the given entity",
  },
});

class NoCheckInField extends Field {
  constructor(data: Omit<FieldProperties, "type">) {
    super({
      customFilterConfig: {
        defaultInstance: "noCheckIn",
        filterInstances: { empty: EmptyFilter, noCheckIn: NoCheckInFilter },
        operators: [...NO_CHECK_INS_FILTER_OPERATORS, FilterOperator.EMPTY],
      },
      features: [
        FieldFeature.FILTERABLE,
        FieldFeature.FILTERABLE_ON_MAP,
        FieldFeature.NON_ADDABLE_FORM_FIELD,
        FieldFeature.NON_IMPORT,
        FieldFeature.NON_INTEGRATION,
        FieldFeature.NON_EXPORT_VIEW,
        FieldFeature.NO_CHECK_IN_FIELD,
      ],
      platformFilterNames: ["lastCheckIn"],
      type: FieldType.NUMBER,
      valueFormatter: (entity: unknown, value: unknown) => {
        const intl = i18nService.getIntl();
        const now = new Date();
        if (!value) {
          return "";
        }
        if (!intl) {
          return differenceInDays(now, value as Date).toString();
        }
        return intl.formatMessage(messages.noCheckIn, {
          days: differenceInDays(now, parseApiDate(value as Date)),
        });
      },
      valueGetter: ["lastCheckIn", "completedAt"],
      ...data,
    });
  }

  convertToPlatformCondition(filterCondition: LegacyFilterCondition): PlatformFilterCondition {
    if (isLegacyCombinedCondition(filterCondition)) {
      return {}; // combined condition is not supported
    } else {
      // Special handling using interval operator for (gt,lt operators), days value is also negative.
      const { operator, value } = filterCondition;
      // a map filter saved before cutoff dates were read as days holds the date as its value,
      // under the date's own operator; the map doesn't re-read it on load, so read it here
      if (typeof value === "string") {
        const platformOperator = filterOperatorToPlatformFilterOperatorMap[operator];
        const storedCutoff = platformOperator
          ? parseCheckInCutoffDateCondition({ [platformOperator]: value })
          : undefined;
        if (storedCutoff) {
          return this.convertToPlatformCondition(storedCutoff);
        }
      }
      // anything else that isn't a number would go out as "NaN days" and fail with a 400
      if (
        ![FilterOperator.EMPTY, FilterOperator.NOT_EMPTY].includes(operator) &&
        !Number.isFinite(value)
      ) {
        loggingService.warning(`Dropped a non-numeric days value for field ${this.name}`, {
          operator,
          value,
        });
        return {};
      }
      if (
        ![FilterOperator.EMPTY, FilterOperator.EQUALS, FilterOperator.NOT_EMPTY].includes(operator)
      ) {
        return {
          [this.platformFilterNames[0]]: {
            [PlatformFilterOperator.INTERVAL]: {
              $round: "days",
              [swappedFilterOperatorToPlatformFilterOperatorMap[operator]!]: `${
                value === 0 ? 0 : -value
              } days`,
            },
          },
        };
      }
      // special handling for the $eq operator
      if (operator === FilterOperator.EQUALS) {
        return {
          [this.platformFilterNames[0]]: {
            [PlatformFilterOperator.INTERVAL]: {
              $round: "days",
              [PlatformFilterOperator.GREATER_THAN_OR_EQUAL]: `${value === 0 ? 0 : -value} days`,
              [PlatformFilterOperator.LESS_THAN_OR_EQUAL]: `${value === 0 ? 0 : -value} days`,
            },
          },
        };
      }
      return {
        [this.platformFilterNames[0]]: getRegularFieldPlatformConditionValue(this, filterCondition),
      };
    }
  }

  convertFromPlatformCondition(
    filterCondition: PlatformFilterCondition
  ): LegacyFilterCondition | undefined {
    if (isCombinedPlatformCondition(filterCondition)) {
      return super.convertFromPlatformCondition(filterCondition);
    } else {
      const platformFilterName = this.platformFilterNames.find((name) => name in filterCondition);
      if (platformFilterName) {
        const fieldCondition = filterCondition[platformFilterName] as Condition;
        if (isIntervalCondition(fieldCondition)) {
          const interval = fieldCondition.$interval as Condition;
          const intervalCondition = parseIntervalCondition(fieldCondition);

          if (PlatformFilterOperator.GREATER_THAN in interval) {
            return {
              operator: FilterOperator.LESS_THAN,
              value: -intervalCondition.value.value!,
            };
          } else if (PlatformFilterOperator.LESS_THAN in interval) {
            return {
              operator: FilterOperator.GREATER_THAN,
              value: -intervalCondition.value.value!,
            };
          } else if (
            PlatformFilterOperator.GREATER_THAN_OR_EQUAL in interval &&
            PlatformFilterOperator.LESS_THAN_OR_EQUAL in interval
          ) {
            return {
              operator: FilterOperator.EQUALS,
              value: -intervalCondition.value.from!,
            };
          } else if (PlatformFilterOperator.LESS_THAN_OR_EQUAL in interval) {
            return {
              operator: FilterOperator.GREATER_THAN_OR_EQUAL,
              value: -intervalCondition.value.value!,
            };
          } else if (PlatformFilterOperator.GREATER_THAN_OR_EQUAL in interval) {
            return {
              operator: FilterOperator.LESS_THAN_OR_EQUAL,
              value: -intervalCondition.value.value!,
            };
          }
        }
        const dateCondition = parseCheckInCutoffDateCondition(fieldCondition);
        if (dateCondition) {
          return dateCondition;
        }
        return getRegularFieldConditionValue(this, { [platformFilterName]: fieldCondition });
      }
      return getRegularFieldConditionValue(this, filterCondition);
    }
  }
}

export default NoCheckInField;
